import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { HashRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';

// Import Providers and Theme
import { MuiThemeProvider } from './components/common/muiTheme';
import { ChatProvider, useChat } from './context/chatContext';

// Import Layout Components
import Header from './components/layout/header';
import Sidebar from './components/layout/sidebar';
import Main from './components/layout/main';

// Import Pitch Component
import Pitch from './components/common/pitch';

import TuneIcon from '@mui/icons-material/Tune';
import Settings from './components/common/Settings';
import ActiveDatasetsDisplay from './components/common/activeDatasets';
import ChainOfThought from './components/chat/chainOfThought';
import GoogleAuthOneTap from './components/common/GoogleAuthOneTap';
import { jwtDecode } from 'jwt-decode';
import { CredentialResponse, GoogleOAuthProvider } from '@react-oauth/google';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import CookiePolicy from './components/legal/CookiePolicy';
import Contact from './components/legal/Contact';
import CookieBanner from './components/common/CookieBanner';
import { registerGoogle, SESSION_TOKEN_KEY } from './services/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Success from './components/common/Success';
import { Cancel } from '@mui/icons-material';
import ErrorBoundary from './components/common/ErrorBoundary';
import ThinkingStreamPanel from './components/common/thinkingStreamPanel';
import PermanentChat from './components/chat/PermanentChat';
import Sync from './components/chat/sync';
import { trackEvent, AnalyticsEvents } from './utils/analytics';


/**
 * The core Chat View component.
 */


/**
 * A new component to contain the main application layout.
 * This ensures hooks like `useNavigate` and `useLocation` are used within the router's context.
 */
const AppLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const showPitch = location.pathname.includes('/pitch');
  const { toggleSettingsModal, setSessionId, setToken, userId, activeChatId, setActiveChatId } = useChat();

  useEffect(() => {
    const stored = localStorage.getItem(SESSION_TOKEN_KEY);
    if (stored && !userId) {
      try {
        const decoded: { sub: string } = jwtDecode(stored);
        setSessionId(decoded.sub);
      } catch (err) {
        console.error('Failed to decode stored credential', err);
        localStorage.removeItem(SESSION_TOKEN_KEY);
      }
    }
  }, [userId, setSessionId]);

  useEffect(() => {
    // @ts-ignore
    window.google?.accounts.id?.prompt((n:any) => {
      console.log('notDisplayed:', n.getNotDisplayedReason?.());
      console.log('skipped:', n.getSkippedReason?.());
      console.log('dismissed:', n.getDismissedReason?.());
    });
  }, []);


  const handleGoogleSuccess = async (res: CredentialResponse) => {
    if (res.credential) {
      //localStorage.setItem(GOOGLE_CREDENTIAL_KEY, res.credential);
      try {
        const decoded: { sub: string; email?: string } = jwtDecode(res.credential);
        setSessionId(decoded.sub);
        if (decoded.email) {
          const { token } = await registerGoogle(decoded.sub, decoded.email, res.credential);
          setToken(token)
        }
        trackEvent(AnalyticsEvents.LoginSuccess, { userId: decoded.sub });
      } catch (err) {
        console.error('Failed to decode credential', err);
      }
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', position: 'relative' }}>
      {!userId || !userId.match(/^\d{21}$/) && <GoogleAuthOneTap onSuccess={handleGoogleSuccess} />}
      {/* Action Buttons */}
        <Box sx={{ position: 'fixed', top: 10, right: 16, zIndex: 2200, display: 'flex', gap: 1 }}>
          <IconButton
              onClick={() => navigate(showPitch ? '/' : '/pitch')}
              sx={{ bgcolor: 'background.paper', boxShadow: 2 }}
              aria-label={showPitch ? "Close Pitch View" : "Show Pitch"}
            >
              <HelpOutlineIcon />
            </IconButton>
            <IconButton
              onClick={() => toggleSettingsModal(true)}
              sx={{ bgcolor: 'background.paper', boxShadow: 2 }}
              aria-label="Open Settings"
            >
              <TuneIcon />
            </IconButton>
        </Box>
      <Header onMenuClick={handleDrawerToggle} pitchShown={showPitch} />
      <Settings />
      <CookieBanner />
      <Routes>
        <Route path="/pitch" element={
          <Box sx={{ flex: 1, width: '100vw', display: 'flex', flexDirection: 'column' }}>
            <Pitch />
          </Box>
        } />
        <Route path="/" element={
          <>
            <Sidebar mobileOpen={mobileOpen} onDrawerToggle={handleDrawerToggle} />
            <Main>
              <PermanentChat />
            </Main>
          </>
        } />
        {/* NEW deep-link route */}
        <Route path="/chat/:chatId" element={<Sync mobileOpen={mobileOpen} onDrawerToggle={handleDrawerToggle} />} />

        <Route path="/privacy" element={<Box sx={{ flex: 1, width: '100vw', overflowY: 'auto' }}><PrivacyPolicy /></Box>} />
        <Route path="/terms" element={<Box sx={{ flex: 1, width: '100vw', overflowY: 'auto' }}><TermsOfService /></Box>} />
        <Route path="/cookies" element={<Box sx={{ flex: 1, width: '100vw', overflowY: 'auto' }}><CookiePolicy /></Box>} />
        <Route path="/contact" element={<Box sx={{ flex: 1, width: '100vw', overflowY: 'auto' }}><Contact /></Box>} />
        <Route path="/success" element={<Box sx={{ flex: 1, width: '100vw', overflowY: 'auto' }}><Success /></Box>} />
      <Route path="/cancel" element={<Box sx={{ flex: 1, width: '100vw', overflowY: 'auto' }}><Cancel /></Box>} />
      </Routes>
    </Box>
  );
};

/**
 * The root component that wraps the App with all necessary context providers.
 */
const AppWrapper: React.FC = () => {
  return (
    <GoogleOAuthProvider clientId={"356497060674-ndjka8l1iod2obja36djpah0fckmaot9.apps.googleusercontent.com"}>
      <MuiThemeProvider>
        <ChatProvider>
          <HashRouter>
            <ErrorBoundary>
                <AppLayout />
              </ErrorBoundary>
          </HashRouter>
        </ChatProvider>
      </MuiThemeProvider>
    </GoogleOAuthProvider>
  );
};

export default AppWrapper;